import { computed } from 'vue'
import { session } from '@/composables/useSession'
import { hasAccountRole, hasWorkspaceRole, hasHighestWorkspaceRole } from '@/utils/roleUtils'
import { useDashboardsList } from '@/composables/useDashboardsList'
import i18n from '@/i18n'
import type { FilterParams } from '../types'
import type { CalibrationFilterParams } from '../calibration/types'

export interface DashboardNavItem {
  key: string
  label: string
  routeName: string
  routeParams?: Record<string, any>
  routeQuery?: Record<string, any>
  testId?: string
  analyticsEvent?: string
  isDisabled?: boolean // For fallback items that aren't clickable
}

interface UseDashboardNavigationOptions {
  filterParams?: Partial<FilterParams & CalibrationFilterParams>
}

export function useDashboardNavigation(options: UseDashboardNavigationOptions = {}) {
  const { filterParams } = options

  const {
    lookerAutoQaDashboard,
    lookerReviewsDashboard,
    lookerReviewsAgentDashboard,
    lookerBotQaDashboard,
    lookerAssignmentsDashboard,
    lookerSurveysDashboard,
    lookerCategoriesDashboard,
    lookerCategoriesAgentDashboard,
    lookerCalibrationDashboard,
    lookerCalibrationAgentDashboard,
    lookerDisputesDashboard,
    lookerDisputesAgentDashboard,
  } = useDashboardsList()

  const defaultQuery = computed(() => ({
    userGroups: filterParams?.userGroups?.length ? filterParams.userGroups.toString() : undefined,
    userIds: filterParams?.userIds?.length ? filterParams.userIds.toString() : undefined,
    workspaceIds: filterParams?.workspaceIds?.length ? filterParams.workspaceIds.toString() : undefined,
  }))

  const isAgentInAllWorkspaces = computed(() => {
    return !hasAccountRole('ADMIN', 'MANAGER') && hasHighestWorkspaceRole('AGENT')
  })

  const canAccessLegacyCalibrationDashboard =
    hasWorkspaceRole('MANAGER', 'LEAD', 'REVIEWER') || (!session.workspace?.role && hasAccountRole('ADMIN', 'MANAGER'))

  // Reactive titles function
  const getTitle = (key: string) => {
    const titleMap: Record<string, string> = {
      'dashboard.reviews': i18n.t('routes.dashboard.label.reviews'),
      'dashboard.auto-qa': i18n.t('routes.dashboard.label.auto_qa'),
      'dashboard.bot-qa': i18n.t('routes.dashboard.label.bot_qa'),
      'dashboard.assignments': i18n.t('routes.dashboard.label.assignments'),
      'dashboard.surveys': i18n.t('routes.dashboard.label.surveys'),
      'dashboard.disputes': i18n.t('routes.dashboard.label.disputes'),
      'dashboard.calibration': i18n.t('routes.dashboard.label.calibration'),
      'dashboard.category': i18n.t('routes.dashboard.label.categories'),
    }
    return titleMap[key] || key
  }

  const navigationItems = computed<DashboardNavItem[]>(() => {
    const items: DashboardNavItem[] = []

    // Reviews Dashboard
    if (
      (session.features.lookerReviewsDashboard || session.features.advancedScorecardsDev) &&
      (lookerReviewsDashboard.value || lookerReviewsAgentDashboard.value)
    ) {
      if (hasWorkspaceRole('AGENT') && lookerReviewsAgentDashboard.value) {
        items.push({
          key: 'dashboard.reviews.agent',
          label: getTitle('dashboard.reviews'),
          routeName: 'dashboard.looker',
          routeParams: { id: lookerReviewsAgentDashboard.value?.id },
          analyticsEvent: 'Looker Agent Reviews dashboard opened',
        })
      } else if (lookerReviewsDashboard.value) {
        items.push({
          key: 'dashboard.reviews.manager',
          label: getTitle('dashboard.reviews'),
          routeName: 'dashboard.looker',
          routeParams: { id: lookerReviewsDashboard.value?.id },
          analyticsEvent: 'Looker Reviews dashboard opened',
        })
      }
    } else {
      items.push({
        key: 'dashboard.reviews.default',
        label: getTitle('dashboard.reviews'),
        routeName: 'dashboard',
        routeQuery: defaultQuery.value,
        analyticsEvent: 'Overview dashboard opened',
      })
    }

    // Auto QA Dashboard
    if (session.features.autoQa && !isAgentInAllWorkspaces.value) {
      if (lookerAutoQaDashboard.value) {
        items.push({
          key: 'dashboard.auto-qa',
          label: getTitle('dashboard.auto-qa'),
          routeName: 'dashboard.looker',
          routeParams: { id: lookerAutoQaDashboard.value?.id },
          analyticsEvent: 'AutoQA dashboard opened',
        })
      } else {
        // Fallback disabled item
        items.push({
          key: 'dashboard.auto-qa.disabled',
          label: getTitle('dashboard.auto-qa'),
          routeName: '',
          isDisabled: true,
        })
      }
    }

    // Bot QA Dashboard
    if (session.features.botQa && !isAgentInAllWorkspaces.value) {
      if (lookerBotQaDashboard.value) {
        items.push({
          key: 'dashboard.bot-qa',
          label: getTitle('dashboard.bot-qa'),
          routeName: 'dashboard.looker',
          routeParams: { id: lookerBotQaDashboard.value?.id },
          analyticsEvent: 'BotQA dashboard opened',
        })
      } else {
        // Fallback disabled item
        items.push({
          key: 'dashboard.bot-qa.disabled',
          label: getTitle('dashboard.bot-qa'),
          routeName: '',
          isDisabled: true,
        })
      }
    }

    // Assignments Dashboard
    if (
      (hasAccountRole('ADMIN', 'MANAGER') || hasHighestWorkspaceRole('MANAGER', 'LEAD')) &&
      lookerAssignmentsDashboard.value
    ) {
      items.push({
        key: 'dashboard.assignments',
        label: getTitle('dashboard.assignments'),
        routeName: 'dashboard.looker',
        routeParams: { id: lookerAssignmentsDashboard.value?.id },
        analyticsEvent: 'Assignments looker dashboard opened',
      })
    }

    // Surveys Dashboard
    if (lookerSurveysDashboard.value) {
      items.push({
        key: 'dashboard.surveys.looker',
        label: getTitle('dashboard.surveys'),
        routeName: 'dashboard.looker',
        routeParams: { id: lookerSurveysDashboard.value?.id },
        analyticsEvent: 'Looker Surveys dashboard opened',
      })
    }

    // Disputes Dashboard
    if (session.features.lookerDisputesDashboard) {
      if (
        (hasAccountRole('ADMIN', 'MANAGER') || hasWorkspaceRole('MANAGER', 'LEAD', 'REVIEWER')) &&
        lookerDisputesDashboard.value
      ) {
        items.push({
          key: 'dashboard.disputes.manager',
          label: getTitle('dashboard.disputes'),
          routeName: 'dashboard.looker',
          routeParams: { id: lookerDisputesDashboard.value?.id },
          analyticsEvent: 'Looker Disputes dashboard opened',
        })
      } else if ((hasAccountRole('USER') || hasWorkspaceRole('AGENT')) && lookerDisputesAgentDashboard.value) {
        items.push({
          key: 'dashboard.disputes.agent',
          label: getTitle('dashboard.disputes'),
          routeName: 'dashboard.looker',
          routeParams: { id: lookerDisputesAgentDashboard.value?.id },
          analyticsEvent: 'Looker Agent Disputes dashboard opened',
        })
      } else {
        // Fallback to legacy if Looker dashboard not available for this role
        items.push({
          key: 'dashboard.disputes.fallback',
          label: getTitle('dashboard.disputes'),
          routeName: 'dashboard.disputes',
          routeQuery: defaultQuery.value,
          analyticsEvent: 'Disputes dashboard opened',
        })
      }
    } else {
      items.push({
        key: 'dashboard.disputes',
        label: getTitle('dashboard.disputes'),
        routeName: 'dashboard.disputes',
        routeQuery: defaultQuery.value,
        analyticsEvent: 'Disputes dashboard opened',
      })
    }

    // Calibration Dashboard
    if (session.features.lookerCalibrationDashboard) {
      // TODO: Until calibration::submit is deployed
      // (see https://zendesk.atlassian.net/browse/ZQA-3428?focusedCommentId=2260253)
      // Assume simpler manager/agent split until above is done
      if (hasWorkspaceRole('AGENT', 'REVIEWER') && lookerCalibrationAgentDashboard.value) {
        items.push({
          key: 'dashboard.calibration.agent',
          label: getTitle('dashboard.calibration'),
          routeName: 'dashboard.looker',
          routeParams: { id: lookerCalibrationAgentDashboard.value?.id },
          analyticsEvent: 'Looker Calibration dashboard opened',
        })
      } else if (hasWorkspaceRole('LEAD', 'MANAGER') && lookerCalibrationDashboard.value) {
        items.push({
          key: 'dashboard.calibration.manager',
          label: getTitle('dashboard.calibration'),
          routeName: 'dashboard.looker',
          routeParams: { id: lookerCalibrationDashboard.value?.id },
          analyticsEvent: 'Looker Calibration dashboard opened',
        })
      }
    } else if (canAccessLegacyCalibrationDashboard) {
      items.push({
        key: 'dashboard.calibration',
        label: getTitle('dashboard.calibration'),
        routeName: 'dashboard.calibration',
        routeQuery: { workspaceIds: defaultQuery.value.workspaceIds },
        analyticsEvent: 'Calibration dashboard opened',
      })
    }

    // Categories Dashboard
    if (lookerCategoriesDashboard.value || lookerCategoriesAgentDashboard.value) {
      if (hasWorkspaceRole('AGENT') && lookerCategoriesAgentDashboard.value) {
        items.push({
          key: 'dashboard.categories.agent',
          label: getTitle('dashboard.category'),
          routeName: 'dashboard.looker',
          routeParams: { id: lookerCategoriesAgentDashboard.value?.id },
          testId: 'categories-dashboard',
          analyticsEvent: 'Looker Categories dashboard opened',
        })
      } else if (lookerCategoriesDashboard.value) {
        items.push({
          key: 'dashboard.categories.manager',
          label: getTitle('dashboard.category'),
          routeName: 'dashboard.looker',
          routeParams: { id: lookerCategoriesDashboard.value?.id },
          testId: 'categories-dashboard',
          analyticsEvent: 'Looker Categories dashboard opened',
        })
      }
    }

    return items
  })

  return {
    navigationItems,
    defaultQuery,
    isAgentInAllWorkspaces,
  }
}
